import { api } from "@/lib/api";
import {
  browserNotificationPreference,
  browserNotificationStartupAction,
  saveBrowserNotificationPreference,
} from "@/lib/browserNotificationPreference";

let publicKey = (import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY || "").trim();
let publicKeyRequest: Promise<string> | null = null;
let serviceWorkerRequest: Promise<ServiceWorkerRegistration> | null = null;
const subscriptionRequests = new Map<string, Promise<WebPushStatus>>();
const preferenceSyncRequests = new Map<string, Promise<WebPushStatus>>();
const syncedEndpoints = new Map<string, string>();

export type WebPushStatus =
  | "subscribed"
  | "unsubscribed"
  | "unsupported"
  | "unconfigured"
  | "denied"
  | "prompt";

export interface BrowserNotificationOptions extends NotificationOptions {
  actionUrl?: string;
}

function supported(): boolean {
  return (
    "Notification" in window &&
    ("pushManager" in window ||
      ("serviceWorker" in navigator && "PushManager" in window))
  );
}

function setEnabledMarker(userId: string, enabled: boolean): void {
  saveBrowserNotificationPreference(userId, enabled ? "enabled" : "disabled");
}

export function wasWebPushEnabled(userId: string): boolean {
  return browserNotificationPreference(userId) === "enabled";
}

export function wasWebPushDisabled(userId: string): boolean {
  return browserNotificationPreference(userId) === "disabled";
}

function windowPushManager(): PushManager | undefined {
  return (window as Window & { pushManager?: PushManager }).pushManager;
}

function resolveServiceWorker(): Promise<ServiceWorkerRegistration> {
  serviceWorkerRequest ||= navigator.serviceWorker.ready.catch((error) => {
    serviceWorkerRequest = null;
    throw error;
  });
  return serviceWorkerRequest;
}

function resolvePushManager(): Promise<PushManager> {
  const directPushManager = windowPushManager();
  if (directPushManager) return Promise.resolve(directPushManager);
  return resolveServiceWorker().then((registration) => registration.pushManager);
}

export async function getWebPushStatus(): Promise<WebPushStatus> {
  if (!supported()) return "unsupported";
  if (Notification.permission === "default") return "prompt";
  if (Notification.permission !== "granted") return "denied";

  const pushManager = await resolvePushManager();
  const subscription = await pushManager.getSubscription();
  return subscription ? "subscribed" : "unsubscribed";
}

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const decoded = window.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(decoded.length));
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

async function resolvePublicKey(): Promise<string> {
  if (publicKey) return publicKey;
  publicKeyRequest ||= api.notifications
    .webPushPublicKey()
    .then((response) => response.publicKey.trim())
    .then((value) => {
      publicKey = value;
      return value;
    })
    .catch((error) => {
      publicKeyRequest = null;
      throw error;
    });
  return publicKeyRequest;
}

interface WebPushPrerequisites {
  publicKey: string;
  pushManager: PushManager;
}

async function resolvePrerequisites(): Promise<WebPushPrerequisites> {
  const [resolvedPublicKey, pushManager] = await Promise.all([
    resolvePublicKey(),
    resolvePushManager(),
  ]);
  return { publicKey: resolvedPublicKey, pushManager };
}

export function warmWebPushPrerequisites(): void {
  if (!supported()) return;
  void resolvePrerequisites().catch((error) => {
    console.warn("Unable to prepare browser notifications.", error);
  });
}

async function synchroniseWebPushSubscription(
  userId: string,
  prerequisites: WebPushPrerequisites,
): Promise<WebPushStatus> {
  const activeRequest = subscriptionRequests.get(userId);
  if (activeRequest) return activeRequest;

  const request = (async () => {
    const { publicKey: resolvedPublicKey, pushManager } = prerequisites;
    if (!resolvedPublicKey) return "unconfigured" as const;

    let subscription = await pushManager.getSubscription();
    if (!subscription) {
      subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(resolvedPublicKey),
      });
    }

    if (syncedEndpoints.get(userId) !== subscription.endpoint) {
      await api.notifications.saveWebPushSubscription({
        userId,
        platform: "web",
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
      });
      syncedEndpoints.set(userId, subscription.endpoint);
    }
    setEnabledMarker(userId, true);
    return "subscribed" as const;
  })();

  subscriptionRequests.set(userId, request);
  void request.then(
    () => subscriptionRequests.delete(userId),
    () => subscriptionRequests.delete(userId),
  );
  return request;
}

export async function ensureWebPushSubscription(
  userId: string,
  requestPermission = false,
  onPermissionGranted?: () => void,
): Promise<WebPushStatus> {
  if (!supported()) return "unsupported";

  // Start the key lookup and push-manager readiness check before the user
  // responds to the permission sheet. Modern WebKit resolves window.pushManager
  // directly; older browsers retain the service-worker fallback.
  const prerequisites = resolvePrerequisites().then(
    (value) => ({ value }),
    (error: unknown) => ({ error }),
  );

  let permission = Notification.permission;
  if (permission === "default" && requestPermission) {
    permission = await Notification.requestPermission();
  }
  if (permission === "default") return "prompt";
  if (permission !== "granted") {
    return "denied";
  }
  // Persist the user's intent before the platform subscription handshake. If
  // iOS terminates the PWA while that handshake is still running, the next
  // launch can finish it silently instead of showing the enable prompt again.
  setEnabledMarker(userId, true);
  onPermissionGranted?.();

  const prepared = await prerequisites;
  if ("error" in prepared) throw prepared.error;
  return synchroniseWebPushSubscription(userId, prepared.value);
}

export function synchroniseWebPushPreference(
  userId: string,
): Promise<WebPushStatus> {
  const activeRequest = preferenceSyncRequests.get(userId);
  if (activeRequest) return activeRequest;

  const request = (async () => {
    const status = await getWebPushStatus();
    const startupAction = browserNotificationStartupAction(
      browserNotificationPreference(userId),
      status,
    );
    if (startupAction === "enable") {
      return ensureWebPushSubscription(userId);
    }
    if (startupAction === "disable") {
      try {
        await removeWebPushSubscription(userId);
      } catch (error) {
        // Keep the saved off state authoritative. A later sync will retry
        // cleanup if a stale platform subscription is still present.
        console.warn(
          "Unable to finish removing the stale browser subscription.",
          error,
        );
      }
      return "unsubscribed";
    }
    return status;
  })();

  preferenceSyncRequests.set(userId, request);
  void request.then(
    () => preferenceSyncRequests.delete(userId),
    () => preferenceSyncRequests.delete(userId),
  );
  return request;
}

export async function showBrowserNotification(
  title: string,
  options: BrowserNotificationOptions = {},
): Promise<boolean> {
  if (
    !("Notification" in window) ||
    Notification.permission !== "granted"
  )
    return false;

  const { actionUrl, ...notificationOptions } = options;
  const data =
    notificationOptions.data &&
    typeof notificationOptions.data === "object"
      ? { ...(notificationOptions.data as Record<string, unknown>) }
      : {};
  if (actionUrl) data.actionUrl = actionUrl;

  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.showNotification(title, {
          ...notificationOptions,
          data,
        });
        return true;
      }
    } catch (error) {
      console.warn(
        "Unable to display a notification through the service worker.",
        error,
      );
    }
  }

  try {
    const notification = new Notification(title, notificationOptions);
    if (actionUrl) {
      notification.onclick = () => {
        window.focus();
        window.location.assign(actionUrl);
      };
    }
    return true;
  } catch (error) {
    // Installed iOS web apps expose Notification but reject its constructor.
    // Keep this failure outside React so it cannot unmount the application.
    console.warn("Unable to display an in-page notification.", error);
    return false;
  }
}

export async function removeWebPushSubscription(
  userId: string,
  options: { preservePreference?: boolean } = {},
): Promise<void> {
  if (!options.preservePreference) setEnabledMarker(userId, false);
  syncedEndpoints.delete(userId);
  if (!supported()) return;
  const pushManager = await resolvePushManager();
  const subscription = await pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  try {
    await api.notifications.removeWebPushSubscription({
      userId,
      platform: "web",
      endpoint,
    });
  } finally {
    await subscription.unsubscribe();
  }
}
